import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import CostCalculatorModal from "@/components/marketing/CostCalculatorModal";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Check, Minus, Sparkles, Info, Target, Calculator, ArrowRight } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const COMPETITOR_ROWS = [
  ["Physical interruption detection", "✓", "–", "–", "–", "Partner-based"],
  ["Behavioural AI refocus", "✓", "–", "–", "–", "–"],
  [
    "Enterprise analytics",
    "✓ (Org Tier)",
    { info: "Basic. RescueTime for Teams provides aggregate time-by-category and productivity-score reports for a team. It does not measure interruptions, refocus time, or organisational focus tiers." },
    "–",
    { info: "Basic. Motion offers team task, project and workload reporting for scheduling purposes. It does not include attention, interruption or refocus analytics." },
    "–",
  ],
  [
    "Privacy-first architecture",
    "✓ (Private by Design)",
    { info: "Partial. RescueTime does not capture screens, but it records application names and website URLs or titles in readable form by default. Tracking can be paused and categories excluded, but data is not one-way hashed." },
    { info: "Partial. Reclaim.ai does not monitor activity; it reads your calendar and task data to schedule. That data is processed in readable form rather than minimised or hashed." },
    { info: "Partial. Motion does not monitor activity or capture screens, but processes calendar, task and project content in readable form to auto-schedule your day." },
    "n/a",
  ],
];

function InfoTick({ text, testId }) {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const show = () => { clearTimeout(timer.current); setOpen(true); };
  const hide = () => { timer.current = setTimeout(() => setOpen(false), 150); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onMouseEnter={show}
          onMouseLeave={hide}
          onClick={() => { clearTimeout(timer.current); setOpen((o) => !o); }}
          className="inline-flex items-center gap-1 mx-auto text-[#1D9E75] hover:text-[#1A3A5C] transition-colors"
          aria-label="More information"
          data-testid={testId}
        >
          <Check size={16} />
          <Info size={13} className="text-[#2C6BAC]" />
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" className="w-72 text-xs text-[#1A1A2E] leading-relaxed" onMouseEnter={show} onMouseLeave={hide} data-testid={`${testId}-content`}>
        {text}
      </PopoverContent>
    </Popover>
  );
}

const TIERS = [
  { id: "spark", name: "Spark", price: 0, label: "Free", users: "1 user", desc: "14-day web trial", cta: "Start free", to: "/register" },
  { id: "focus-web", name: "Focus Web", price: 14, users: "1 user", desc: "Web-only · 9 sites", cta: "Choose Focus Web", to: "/register" },
  { id: "focus", name: "Focus", price: 23, users: "1 user", desc: "Web + 3 local apps", cta: "Choose Focus", to: "/register", featured: true },
  { id: "sync-web", name: "Sync Web", price: 18, perUser: true, users: "3–24 users", desc: "Web · teams", cta: "Choose Sync Web", to: "/register" },
  { id: "sync", name: "Sync", price: 28, perUser: true, users: "3–24 users", desc: "Web + 5 local apps", cta: "Choose Sync", to: "/register" },
  { id: "momentum", name: "Momentum", price: 38, perUser: true, users: "25–100 users", desc: "Up to 9 apps · 20 sites", cta: "Choose Momentum", to: "/register" },
  { id: "command", name: "Command", price: null, users: "101+ users", desc: "Enterprise · negotiable", cta: "Contact us", to: "/contact" },
];

const FEATURE_ROWS = [
  { feature: "Refocus widget (corner / half / full)", values: ["✓", "✓", "✓", "✓", "✓", "✓", "✓"] },
  { feature: "Web monitoring", values: ["✓", "✓", "✓", "✓", "✓", "✓", "✓"] },
  { feature: "Local app monitoring", values: ["–", "–", "3", "–", "5", "9", "15+"] },
  { feature: "Sites in hierarchy", values: ["9", "9", "9", "15", "15", "20", "30+"] },
  { feature: "Weekly reports", values: ["✓", "✓", "✓", "✓", "✓", "✓", "✓"] },
  { feature: "Org Tier reporting", values: ["–", "–", "–", "✓", "✓", "✓", "✓"] },
  { feature: "AI-driven reports cadence", values: ["–", "–", "–", "Quarterly", "Quarterly", "Fortnightly", "Weekly"] },
  { feature: "Support Manager", values: ["–", "–", "–", "✓", "✓", "✓", "✓"] },
  { feature: "Account Management Team", values: ["–", "–", "–", "–", "–", "✓", "24/7"] },
  { feature: "Compass Feature: Real-time AI insights, assistance and enhanced reports", values: ["Add-on", "Add-on", "Add-on", "Add-on", "Add-on", "Add-on", "✓"] },
];

function TierTarget({ testId }) {
  return (
    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-[#1D9E75]/15 text-[#1D9E75]" title="Selected in your calculator" data-testid={testId}>
      <Target size={14} className="tier-target" />
    </span>
  );
}

export default function Pricing() {
  const [annual, setAnnual] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);
  const [params] = useSearchParams();
  const targetTier = params.get("tier");
  useEffect(() => {
    if (!targetTier) return;
    const el = document.getElementById(`pricing-card-${targetTier}`);
    if (el) setTimeout(() => el.scrollIntoView({ block: "center", behavior: "smooth" }), 150);
  }, [targetTier]);
  const price = (t) => {
    if (t.price === null) return "Custom";
    if (t.price === 0) return "Free";
    // Annual = pay for 10 months, get 12 (2 months free)
    const v = annual ? t.price * 10 : t.price;
    return `$${v}`;
  };
  const cadence = (t) => {
    if (t.price === null) return "/contract";
    if (t.price === 0) return "for 14 days";
    return annual ? "/year" + (t.perUser ? " · per user" : "") : "/mo" + (t.perUser ? " · per user" : "");
  };

  return (
    <MarketingLayout>
      <section className="pt-24 pb-12 max-w-7xl mx-auto px-6 lg:px-10">
        <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-4">Pricing</p>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <h1 className="font-display text-5xl lg:text-6xl tracking-tighter text-[#1A1A2E] leading-[0.95] max-w-3xl">
            One platform.<br />From solo focus to enterprise insight.
          </h1>
          <div className="inline-flex items-center gap-3 bg-white border border-[#E8ECF2] rounded-full px-4 py-2 self-start">
            <span className={`text-sm ${!annual ? "text-[#1A1A2E] font-semibold" : "text-[#555568]"}`}>Monthly</span>
            <Switch checked={annual} onCheckedChange={setAnnual} data-testid="pricing-billing-toggle" />
            <span className={`text-sm ${annual ? "text-[#1A1A2E] font-semibold" : "text-[#555568]"}`}>
              Annual <span className="text-[#1D9E75] text-xs font-semibold">· 2 months free</span>
            </span>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 mb-8">
        <div className="rounded-xl bg-[#1A3A5C] text-white p-6 lg:p-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid="annual-banner">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#E8ECF2]/70 font-semibold mb-1">Annual billing</p>
            <h2 className="font-display text-2xl lg:text-3xl tracking-tight">
              Pay for 10 months. Get 12. Keep the confidence.
            </h2>
            <p className="text-sm text-[#E8ECF2]/80 mt-1 max-w-xl">
              Switch the toggle above to Annual - your two free months land automatically at checkout. No coupon code, no haggle.
            </p>
          </div>
          <button
            onClick={() => setAnnual(true)}
            className="bg-white text-[#1A3A5C] hover:bg-[#E8ECF2] px-5 py-2 rounded-md font-semibold transition-colors text-sm shrink-0"
            data-testid="annual-banner-cta"
          >
            Show me annual prices
          </button>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {TIERS.map((t) => (
            <div
              key={t.id}
              id={`pricing-card-${t.id}`}
              data-testid={`pricing-card-${t.id}`}
              className={`relative rounded-xl border p-7 flex flex-col ${
                t.featured ? "bg-[#1A3A5C] text-white border-[#1A3A5C] tracing-beam lg:scale-[1.02]" : "bg-white border-[#E8ECF2] text-[#1A1A2E]"
              } ${targetTier === t.id ? "ring-2 ring-[#1D9E75] ring-offset-2 ring-offset-[#F8F9FC]" : ""}`}
            >
              {t.featured && (
                <span className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.18em] bg-[#2C6BAC] text-white px-2 py-1 rounded">
                  Most popular
                </span>
              )}
              <p className={`text-xs uppercase tracking-[0.18em] ${t.featured ? "opacity-70" : "text-[#2C6BAC]"} font-semibold`}>WYLO</p>
              <h3 className="font-display text-2xl font-bold mt-1 flex items-center gap-2">{t.name}{targetTier === t.id && <TierTarget testId={`tier-target-card-${t.id}`} />}</h3>
              <p className={`text-xs mt-1 ${t.featured ? "text-[#E8ECF2]/80" : "text-[#555568]"}`}>{t.users}</p>

              <div className="mt-6">
                <span className="font-display text-4xl font-bold">{price(t)}</span>
                <span className={`ml-1 text-xs ${t.featured ? "text-[#E8ECF2]/80" : "text-[#555568]"}`}>{cadence(t)}</span>
              </div>
              <p className={`mt-3 text-sm ${t.featured ? "text-[#E8ECF2]/80" : "text-[#555568]"}`}>{t.desc}</p>

              <Link to={t.to} className="mt-auto pt-6">
                <Button
                  className={`w-full ${t.featured ? "bg-white text-[#1A3A5C] hover:bg-[#E8ECF2]" : "bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white"}`}
                  data-testid={`pricing-cta-${t.id}`}
                >
                  {t.cta}
                </Button>
              </Link>
            </div>
          ))}

          {/* Compass add-on */}
          <div className="rounded-xl border border-[#E8ECF2] bg-white text-[#1A1A2E] p-7 flex flex-col">
            <p className="text-xs uppercase tracking-[0.18em] text-[#2C6BAC] font-semibold">Add-on</p>
            <h3 className="font-display text-2xl font-bold mt-1 flex items-center gap-2">
              <Sparkles size={18} className="text-[#BA7517]" />
              Compass
            </h3>
            <p className="text-xs mt-1 text-[#555568]">AI Assistant · any paid tier</p>
            <div className="mt-6">
              <span className="font-display text-4xl font-bold">$9</span>
              <span className="ml-1 text-xs text-[#555568]">/user/mo</span>
            </div>
            <p className="mt-3 text-sm text-[#555568]">Behavioural modelling, AI-enhanced reports, AI support chatbot.</p>
            <Link to="/register" className="mt-auto pt-6">
              <Button variant="outline" className="w-full border-[#1A3A5C]">Add Compass</Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-10">
        <div className="rounded-xl border border-[#E8ECF2] bg-white p-6 lg:p-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid="pricing-calculator-banner">
          <div className="flex items-start gap-4">
            <div className="h-11 w-11 shrink-0 rounded-md bg-[#1A3A5C] text-white flex items-center justify-center"><Calculator size={18} /></div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-1">Is it worth it?</p>
              <h2 className="font-display text-xl lg:text-2xl tracking-tight text-[#1A1A2E]">Compare a tier's cost against what interruptions cost you.</h2>
              <p className="text-sm text-[#555568] mt-1 max-w-xl">Same calculator as the home page: your off-focus cost, the refocus cost of a tier, and the value WYLO returns at just 40% efficiency.</p>
            </div>
          </div>
          <Button onClick={() => setCalcOpen(true)} className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white h-11 px-6 shrink-0" data-testid="pricing-open-calculator">
            Run the numbers <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>
      <CostCalculatorModal open={calcOpen} onOpenChange={setCalcOpen} />

      {/* Feature comparison */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-24">
        <h2 className="font-display text-3xl lg:text-4xl tracking-tighter text-[#1A1A2E] mb-8">
          Feature comparison
        </h2>
        <div className="overflow-x-auto rounded-xl border border-[#E8ECF2] bg-white">
          <table className="w-full text-sm" data-testid="pricing-feature-table">
            <thead className="bg-[#F8F9FC] text-[#555568]">
              <tr>
                <th className="text-left p-4 font-medium">Feature</th>
                {TIERS.map((t) => (
                  <th key={t.id} className="p-4 font-display font-bold text-[#1A1A2E]">
                    <span className="inline-flex items-center gap-1.5">{t.name}{targetTier === t.id && <TierTarget testId={`tier-target-table-${t.id}`} />}</span>
                    {t.id === "spark" && <span className="block text-[10px] font-normal normal-case tracking-normal text-[#555568] mt-0.5" data-testid="spark-trial-note">(14 day trial)</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FEATURE_ROWS.map((row, i) => (
                <tr key={i} className="border-t border-[#E8ECF2]">
                  <td className="p-4 text-[#1A1A2E]">{row.feature}</td>
                  {row.values.map((v, j) => (
                    <td key={j} className={`p-4 text-center font-mono text-xs ${targetTier === TIERS[j].id ? "bg-[#1D9E75]/5" : ""}`}>
                      {v === "✓" ? <Check size={16} className="text-[#1D9E75] mx-auto" /> :
                       v === "–" ? <Minus size={16} className="text-[#E8ECF2] mx-auto" /> :
                       v === "Coming" ? <span className="text-[#BA7517] uppercase tracking-wider">Coming</span> :
                       v === "Add-on" ? <span className="text-[#BA7517] uppercase tracking-wider whitespace-nowrap" data-testid={`compass-addon-${j}`}>Paid add-on</span> :
                       <span className="text-[#1A1A2E]">{v}</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Competitor comparison */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-24">
        <h2 className="font-display text-3xl lg:text-4xl tracking-tighter text-[#1A1A2E] mb-2">
          WYLO vs. the rest
        </h2>
        <p className="text-[#555568] mb-8">An honest comparison across the focus & productivity category.</p>
        <div className="overflow-x-auto rounded-xl border border-[#E8ECF2] bg-white">
          <table className="w-full text-sm" data-testid="competitor-table">
            <thead className="bg-[#F8F9FC] text-[#555568]">
              <tr>
                <th className="text-left p-4 font-medium">Dimension</th>
                <th className="p-4 font-display font-bold text-[#1A3A5C] text-center">WYLO</th>
                <th className="p-4 font-display font-bold">RescueTime</th>
                <th className="p-4 font-display font-bold">Reclaim.ai</th>
                <th className="p-4 font-display font-bold">Motion</th>
                <th className="p-4 font-display font-bold">Focusmate</th>
              </tr>
            </thead>
            <tbody>
              {COMPETITOR_ROWS.map((r, i) => (
                <tr key={i} className="border-t border-[#E8ECF2]">
                  {r.map((c, j) => (
                    <td key={j} className={`p-4 ${j === 1 ? "text-[#1A3A5C] font-semibold text-center" : j === 0 ? "text-[#1A1A2E]" : "text-[#555568] text-center"}`}>
                      {typeof c === "object" && c !== null
                        ? <InfoTick text={c.info} testId={`competitor-info-${i}-${j}`} />
                        : c === "✓" ? <Check size={16} className="text-[#1D9E75] mx-auto" />
                        : c === "–" ? <Minus size={16} className="text-[#E8ECF2] mx-auto" />
                        : c}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-[#555568] mt-3" data-testid="competitor-complement">WYLO may be a good complement to any of these solutions if you already have them.</p>
      </section>

      <div className="h-24" />
    </MarketingLayout>
  );
}
